import type { ReactNode } from "react";
import { Icon, type IconName } from "./icons";

export type NavItem = {
  id: string;
  label: string;
  icon: IconName;
  href?: string;
  active?: boolean;
  onClick?: () => void;
};

export function AppShell({
  brand,
  navigation,
  topbar,
  children
}: {
  brand?: ReactNode;
  navigation: NavItem[];
  topbar?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="nx-app-shell">
      <aside className="nx-sidebar">
        <div className="nx-sidebar__brand">{brand ?? <>Leylion</>}</div>
        <nav className="nx-sidebar__nav" aria-label="Primary navigation">
          {navigation.map((item) => item.href ? (
            <a
              key={item.id}
              href={item.href}
              className="nx-sidebar__item"
              aria-current={item.active ? "page" : undefined}
              aria-label={item.label}
              title={item.label}
              data-nav-id={item.id}
            >
              <Icon name={item.icon} />
              <span className="nx-sidebar__label">{item.label}</span>
            </a>
          ) : (
            <button
              key={item.id}
              type="button"
              className="nx-sidebar__item"
              aria-current={item.active ? "page" : undefined}
              aria-label={item.label}
              title={item.label}
              data-nav-id={item.id}
              onClick={item.onClick}
            >
              <Icon name={item.icon} />
              <span className="nx-sidebar__label">{item.label}</span>
            </button>
          ))}
        </nav>
      </aside>
      <header className="nx-topbar">{topbar}</header>
      <main className="nx-main">
        <div className="nx-main__content">{children}</div>
      </main>
    </div>
  );
}

export function Breadcrumbs({
  items,
  label = "Breadcrumb"
}: {
  items: Array<{ label: ReactNode; href?: string }>;
  label?: string;
}) {
  return (
    <nav className="nx-breadcrumbs" aria-label={label}>
      {items.map((item, index) => (
        <span key={index}>
          {item.href ? <a href={item.href}>{item.label}</a> : <span aria-current="page">{item.label}</span>}
          {index < items.length - 1 ? <span aria-hidden="true">/</span> : null}
        </span>
      ))}
    </nav>
  );
}

export function PageHeader({
  eyebrow,
  title,
  description,
  actions,
  breadcrumbs
}: {
  eyebrow?: ReactNode;
  title: ReactNode;
  description?: ReactNode;
  actions?: ReactNode;
  breadcrumbs?: Array<{ label: ReactNode; href?: string }>;
}) {
  return (
    <header className="nx-page-header">
      <div>
        {breadcrumbs ? <Breadcrumbs items={breadcrumbs} /> : null}
        {eyebrow ? <div className="nx-field__optional">{eyebrow}</div> : null}
        <h1>{title}</h1>
        {description ? <p>{description}</p> : null}
      </div>
      {actions ? <div className="nx-page-header__actions">{actions}</div> : null}
    </header>
  );
}
